import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import {
  validateDateOfBirth,
  validateDateThatIsAfter,
} from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { inviteEmployeesToGroup } from 'api/operations/userService';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ApolloError } from '@apollo/client/errors';
import { ServerError } from '@apollo/client';

interface SelfRegistrationFields extends DefaultFormFields {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  dob: FormControl<string>;
}

interface SelfRegistrationPageStoreParams {
  groupId: string;
}

export class SelfRegistrationPageStore {
  private _isLoading = false;
  private _groupId: string;
  private _isUserInTheSystem = false;
  private _isUserInvited = false;

  public formController: FormController<SelfRegistrationFields> = new FormController({
    firstName: new FormControl<string>('', { validations: ['printableCharacters'], optional: false, trim: true }),
    lastName: new FormControl<string>('', { validations: ['printableCharacters'], optional: false, trim: true }),
    email: new FormControl('', { validations: ['isEmailValid'], optional: false }),
    dob: new FormControl<string>('', {
      optional: false,
      customValidations: [
        {
          check: (value: string): boolean => validateDateOfBirth(value, 120),
          msg: 'Incorrect date picked',
        },
        {
          check: (value: string): boolean => !validateDateThatIsAfter(value, `${new Date().getFullYear() - 13}-01-01`), // validation checks that EE is older than 14
          msg: 'You must be at least 14 years old to register',
        },
      ],
    }),
  });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isUserInTheSystem(): boolean {
    return this._isUserInTheSystem;
  }

  public get isUserInvited(): boolean {
    return this._isUserInvited;
  }

  private _setLoading(value: boolean): void {
    this._isLoading = value;
  }

  private _setIsUserInTheSystem(value: boolean): void {
    this._isUserInTheSystem = value;
  }

  private _setIsUserInvited(value: boolean): void {
    this._isUserInvited = value;
  }

  public async inviteEmployeeToGroup(): Promise<void> {
    this._setLoading(true);

    await runInAction(async (): Promise<void> => {
      if (!this.formController.basicFieldsValidationsPassed) {
        throw new Error('Attempted to submit invalid form!');
      }
      try {
        await inviteEmployeesToGroup(this._groupId, [
          this.formController.getFormDataObject() as Record<string, string>,
        ]);
        runInAction(() => {
          this._setLoading(false);
          this._setIsUserInvited(true);
          toastManager.addToast(
            'Invitation sent successfully! Check your email for the registration link.',
            ToastColorTypes.SUCCESS,
          );
        });
      } catch (error) {
        const networkError = (error as ApolloError)?.networkError as ServerError;
        runInAction(() => {
          this._setLoading(false);
          if (networkError?.statusCode === 409) {
            this._setIsUserInTheSystem(true);
            this.formController.resetForm();
          } else {
            toastManager.addToast('Something went wrong during invite', ToastColorTypes.WARNING);
          }
        });
      }
    });
  }

  constructor({ groupId }: SelfRegistrationPageStoreParams) {
    this._groupId = groupId;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new SelfRegistrationPageStore({ groupId: 'mock-group-id' }));

export const useSelfRegistrationPageStore = (): SelfRegistrationPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
